import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebase';

/**
 * Wraps a route that requires authentication.
 * - If both `allowedEmails` and `requiredClaim` are provided, either grants access
 * - If only one is provided, that requirement must be satisfied
 * - Otherwise any authenticated user can access
 */
const ProtectedRoute = ({ children, allowedEmails = null, requiredClaim = null, redirectTo = '/adminlogin' }) => {
  const [authState, setAuthState] = useState('loading'); // 'loading' | 'authenticated' | 'unauthorized'

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setAuthState('unauthorized');
      } else {
        try {
          const token = requiredClaim ? await user.getIdTokenResult(true) : null;
          const emailAllowed = allowedEmails?.includes(user.email?.toLowerCase()) === true;
          const claimAllowed = requiredClaim ? token?.claims?.[requiredClaim] === true : false;
          const authorized = allowedEmails && requiredClaim
            ? emailAllowed || claimAllowed
            : allowedEmails
              ? emailAllowed
              : requiredClaim
                ? claimAllowed
                : true;
          setAuthState(authorized ? 'authenticated' : 'unauthorized');
        } catch (error) {
          console.error('Could not verify route permissions:', error);
          setAuthState('unauthorized');
        }
      }
    });

    return () => unsubscribe();
  }, [allowedEmails, requiredClaim]);

  if (authState === 'loading') {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (authState === 'unauthorized') {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

export default ProtectedRoute;
